import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { QUESTIONS } from "@/lib/coreAssessment";
import { Lock, ArrowRight, LogOut, Sparkles } from "lucide-react";

// Non-dismissible gate: a brand-new salesperson is locked out of the whole app until they
// finish their C.O.R.E. assessment. Only two actions: start/continue the assessment, or log out.
// Hidden on the assessment page itself so they can actually take it (salesman-only, never business).
export default function CoreGate() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [answered, setAnswered] = useState(0);

  const gated = user?.role === "salesman" && !user?.core_done && !user?.impersonating;

  useEffect(() => {
    if (!gated) return;
    api.get("/core/progress").then((r) => {
      const a = r.data?.answers;
      setAnswered(Array.isArray(a) ? a.length : 0);
    }).catch(() => {});
  }, [gated, location.pathname]);

  if (!gated) return null;
  if (location.pathname.replace(/\/+$/, "") === "/core-assessment") return null; // let them take it

  const resuming = answered > 0;

  return (
    <div data-testid="core-gate" className="fixed inset-0 z-[100] mesh-bg force-dark flex items-center justify-center p-5 overflow-y-auto">
      <motion.div initial={{ opacity: 0, y: 24, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.4 }}
        className="w-full max-w-md text-center">
        <div className="relative inline-block">
          <div aria-hidden className="absolute inset-4 -z-10 blur-3xl opacity-40 bg-gradient-to-br from-violet-600 to-emerald-500 rounded-full" />
          <motion.img src="/connie-avatar.png" alt="Connie, your sales guide"
            animate={{ y: [0, -8, 0] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            className="w-28 h-28 sm:w-32 sm:h-32 object-contain mx-auto drop-shadow-2xl" />
        </div>
        <span className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.2em] text-violet-300">
          <Sparkles className="w-3.5 h-3.5" /> Connie · C.O.R.E. Sales Assessment
        </span>

        <div className="glass rounded-3xl p-7 sm:p-8 mt-5">
          <div className="w-14 h-14 rounded-2xl bg-violet-600/20 border border-violet-400/30 flex items-center justify-center mx-auto">
            <Lock className="w-6 h-6 text-violet-300" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-black text-white mt-4">
            {resuming ? "Let's finish your C.O.R.E." : "One quick step to unlock Salesman Connect"}
          </h1>
          <p className="text-slate-300 mt-3 leading-relaxed">
            {resuming
              ? <>You're on <span className="text-white font-semibold">Question {Math.min(answered + 1, QUESTIONS.length)} of {QUESTIONS.length}</span>. Pick up right where you left off, your answers are saved.</>
              : <>Before you can browse jobs and get discovered by companies, take your one-time C.O.R.E. sales assessment. It only takes 5–10 minutes.</>}
          </p>
          <Button data-testid="core-gate-start" onClick={() => navigate("/core-assessment")}
            className="mt-6 w-full rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white font-black text-lg h-14 shadow-lg shadow-violet-900/40">
            {resuming ? "Continue my assessment" : "Start my C.O.R.E. assessment"} <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <button data-testid="core-gate-logout" onClick={logout}
            className="mt-4 inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white">
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      </motion.div>
    </div>
  );
}
